import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { IconStyle } from '../../icons/Icons';
import { TextColors } from '../../styles/Colors';
import { DefaultFocusIndicator, FocusIndicatorWidth } from '../../styles/FocusIndicators';
import { PreventUserSelect } from '../../styles/PreventUserSelect';
import { typedKeys } from '../../utils/TypedKeys';

export type ExpandoType = 'default' | 'neutral' | 'red' | 'yellow' | 'green' | 'blue' | 'violet';
export const ExpandoChevronSize = 14;

const DefaultContentColor = TextColors.Primary;

type CSSVarNames =
`--expando-button-background-color${'' | '-hover' | '-press'}` |
`--expando-button-color${'' | '-hover' | '-press'}` |
`--expando-content-background-color`;

const CSSVariables: { [key in ExpandoType]: { [key in CSSVarNames]: string } } = Object.freeze({
  default: {
    '--expando-button-background-color': 'transparent',
    '--expando-button-background-color-hover': '#E5E5E5',
    '--expando-button-background-color-press': '#C9C9C9',
    '--expando-button-color': DefaultContentColor,
    '--expando-button-color-hover': DefaultContentColor,
    '--expando-button-color-press': DefaultContentColor,
    '--expando-content-background-color': 'transparent',
  },
  neutral: {
    '--expando-button-background-color': 'transparent',
    '--expando-button-background-color-hover': '#E5E5E5',
    '--expando-button-background-color-press': '#C9C9C9',
    '--expando-button-color': DefaultContentColor,
    '--expando-button-color-hover': DefaultContentColor,
    '--expando-button-color-press': DefaultContentColor,
    '--expando-content-background-color': '#F3F3F3',
  },
  red: {
    '--expando-button-background-color': '#FEDED8',
    '--expando-button-background-color-hover': '#FEB8AB',
    '--expando-button-background-color-press': '#FE8F7D',
    '--expando-button-color': '#BA0517',
    '--expando-button-color-hover': '#8E030F',
    '--expando-button-color-press': '#300C01',
    '--expando-content-background-color': '#FEDED8',
  },
  yellow: {
    '--expando-button-background-color': '#F9E3B6',
    '--expando-button-background-color-hover': '#FCC003',
    '--expando-button-background-color-press': '#E4A201',
    '--expando-button-color': '#6F3400',
    '--expando-button-color-hover': '#4F2100',
    '--expando-button-color-press': '#281202',
    '--expando-content-background-color': '#F9E3B6',
  },
  green: {
    '--expando-button-background-color': '#CDEFC4',
    '--expando-button-background-color-hover': '#91DB8B',
    '--expando-button-background-color-press': '#45C65A',
    '--expando-button-color': '#22683E',
    '--expando-button-color-hover': '#194E31',
    '--expando-button-color-press': '#0E3522',
    '--expando-content-background-color': '#CDEFC4',
  },
  blue: {
    '--expando-button-background-color': '#CFE9FE',
    '--expando-button-background-color-hover': '#90D0FE',
    '--expando-button-background-color-press': '#1AB9FF',
    '--expando-button-color': '#05628A',
    '--expando-button-color-hover': '#084968',
    '--expando-button-color-press': '#023248',
    '--expando-content-background-color': '#CFE9FE',
  },
  violet: {
    '--expando-button-background-color': '#F2DEFE',
    '--expando-button-background-color-hover': '#E5B9FE',
    '--expando-button-background-color-press': '#D892FE',
    '--expando-button-color': '#9602CF',
    '--expando-button-color-hover': '#730394',
    '--expando-button-color-press': '#520066',
    '--expando-content-background-color': '#F2DEFE',
  },
});
export const ExpandoColorStyleNames: Record<ExpandoType, string> = Object.freeze(typedKeys(CSSVariables).reduce<Record<ExpandoType, string>>(
  (names, type) => (names[type] = TypeStyle.style(CSSVariables[type] as React.CSSProperties), names), {} as Record<ExpandoType, string>)
);

export const ExpandoStyleNames = (
  {
    rootStyleName: TypeStyle.style({
      ['--expando-accent-color' as string]: 'var(--expando-button-color)',
      ['--expando-border-radius' as string]: 2,
      ['--expando-chevron-color' as string]: TextColors.Primary,
      ['--expando-chevron-size' as string]: ExpandoChevronSize,
      alignItems: 'stretch',
      display: 'flex',
      flexDirection: 'column',
    }),
    buttonStyleName: TypeStyle.style({
      ...PreventUserSelect,
      border: 'none',
      borderRadius: 'var(--expando-border-radius)',
      boxSizing: 'border-box',
      color: `var(--expando-button-color)`,
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'inherit',
      fontSize: 12,
      justifyContent: 'center',
      minHeight: 24,
      paddingBottom: 0,
      paddingLeft: 4,
      paddingRight: 4,
      paddingTop: 0,
      position: 'relative', // necessary for focus indicator
      textAlign: 'left',
      $nest: {
        '&:focus-visible': DefaultFocusIndicator(),
        '&:focus-visible::after': {
          border: `1px solid white`,
          content: `" "`,
          inset: FocusIndicatorWidth,
          position: 'absolute',
        },
        '&.hasLeftBorder:focus-visible::after': {
          left: -2,
        },

        // up/down appearance
        '&.down-direction[aria-expanded="true"]': {
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        },
        '&.up-direction[aria-expanded="true"]': {
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
        },
        '&.up-direction': {
          order: 1,
        },

        // bg color
        '&.noBackgroundWhenExpanded[aria-expanded="true"]': {
          backgroundColor: 'transparent',
        },
        '&:not([aria-expanded="true"])': {
          backgroundColor: 'var(--expando-button-background-color)',
        },
        '&[aria-expanded="true"]': {
          backgroundColor: 'var(--expando-content-background-color)',
        },
        '&:enabled:hover': {
          backgroundColor: 'var(--expando-button-background-color-hover)',
          color: `var(--expando-button-color-hover)`,
        },
        '&:enabled:active': {
          backgroundColor: 'var(--expando-button-background-color-press)',
          color: `var(--expando-button-color-press)`,
        },

        // left border (and spacing)
        '&.hasLeftBorder': {
          borderLeft: `4px solid var(--expando-accent-color)`,
        },
        '&.hasLeftBorder.left-align': {
          paddingRight: 8,
        },
        '&.hasLeftBorder.right-align': {
          paddingLeft: 8,
        },

        // button contents (container of chevron icon and label icon and label)
        '& > :first-child': {
          alignItems: 'flex-start',
          display: 'flex',
          marginBottom: 4,
          marginTop: 4,
        },
        '&.hasLeftBorder > :first-child': {
          fontWeight: 'bold',
        },
        '&.right-align > :first-child': {
          width: '100%',
        },
      },
    }),
    labelIconStyleName: TypeStyle.style({
      ...IconStyle,
      display: 'inline-flex',
      marginRight: 4,
    }),
    labelStyleName: TypeStyle.style({
      flexGrow: 1,
    }),
    subLabelStyleName: TypeStyle.style({
      marginBottom: 4,
      $nest: {
        '.left-align > &': {
          marginLeft: 22,
        },
      },
    }),
    contentStyleName: TypeStyle.style({
      backgroundColor: 'var(--expando-content-background-color)',
      color: DefaultContentColor,
      fontSize: 12,
      lineHeight: '18px',
      paddingLeft: 4,
      paddingRight: 4,
      $nest: {
        '&.down-direction': {
          borderBottomLeftRadius: 'var(--expando-border-radius)',
          borderBottomRightRadius: 'var(--expando-border-radius)',
          paddingBottom: 8,
          paddingTop: 4,
        },
        '&.up-direction': {
          borderTopLeftRadius: 'var(--expando-border-radius)',
          borderTopRightRadius: 'var(--expando-border-radius)',
          paddingBottom: 4,
          paddingTop: 8,
        },
        '&.hasLeftBorder': {
          borderLeft: `4px solid var(--expando-accent-color)`,
          paddingLeft: 8,
          paddingRight: 8,
        },
        '&.left-align': {
          paddingLeft: 26,
        },
        '&.noBackgroundWhenExpanded': {
          backgroundColor: 'transparent',
        },
      },
    }),
    legacyAlignmentStyleName: TypeStyle.style({ alignItems: 'flex-start' }), // this must stay at the end so the rule wins
  }
);
